import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {LanguageLocale} from '../../constants/locales';

export enum ContentWidth {
    BOXED = 'boxed',
    FULL = 'full',
}

export enum RouterTransition {
    FADE_IN = 'fadeIn',
    FADE_IN_LEFT = 'fadeInLeft',
    ZOOM_IN = 'zoomIn',
    NONE = 'none',
}

export interface ISagaState {
    activeLanguage: LanguageLocale;
    contentWidth: ContentWidth;
    routerTransition: RouterTransition;
    menuCollapsed: boolean;
    menuHidden: boolean;
    showLoader: boolean;
}

export interface IChangeLanguage {
    activeLanguage: LanguageLocale;
}

export interface IChangeShowLoader {
    readonly payload: {
        showLoader: boolean;
    };
}

const initialState: ISagaState = {
    activeLanguage: LanguageLocale.PL,
    contentWidth: ContentWidth.BOXED,
    routerTransition: RouterTransition.FADE_IN,
    menuCollapsed: false,
    menuHidden: false,
    showLoader: false,
};

const sagaSlice = createSlice({
    name: 'saga',
    initialState: initialState,
    reducers: {
        changeActiveLanguage: {
            reducer: (state: ISagaState, action: PayloadAction<IChangeLanguage>) => {
                return {
                    ...state,
                    activeLanguage: action.payload.activeLanguage,
                };
            },
            prepare(activeLanguage: LanguageLocale) {
                return {
                    payload: {activeLanguage: activeLanguage},
                };
            },
        },
        changeContentWidth: {
            reducer: (state: ISagaState, action: PayloadAction<{contentWidth: ContentWidth}>) => {
                return {
                    ...state,
                    contentWidth: action.payload.contentWidth,
                };
            },
            prepare(contentWidth: ContentWidth) {
                return {
                    payload: {contentWidth: contentWidth},
                };
            },
        },
        changeRouterTransition: {
            reducer: (state: ISagaState, action: PayloadAction<{routerTransition: RouterTransition}>) => {
                return {
                    ...state,
                    routerTransition: action.payload.routerTransition,
                };
            },
            prepare(routerTransition: RouterTransition) {
                return {
                    payload: {routerTransition: routerTransition},
                };
            },
        },
        changeMenuCollapsed: {
            reducer: (state: ISagaState, action: PayloadAction<{isMenuCollapsed: boolean}>) => {
                return {
                    ...state,
                    menuCollapsed: action.payload.isMenuCollapsed,
                };
            },
            prepare(isMenuCollapsed: boolean) {
                return {
                    payload: {isMenuCollapsed: isMenuCollapsed},
                };
            },
        },
        changeMenuHidden: {
            reducer: (state: ISagaState, action: PayloadAction<{isMenuHidden: boolean}>) => {
                return {
                    ...state,
                    menuHidden: action.payload.isMenuHidden,
                };
            },
            prepare(isMenuHidden: boolean) {
                return {
                    payload: {isMenuHidden: isMenuHidden},
                };
            },
        },
        changeShowLoader: {
            reducer: (state: ISagaState, action: IChangeShowLoader) => {
                return {
                    ...state,
                    showLoader: action.payload.showLoader,
                };
            },
            prepare(showLoader: boolean) {
                return {
                    payload: {showLoader: showLoader},
                };
            },
        },
    },
});

export const {changeActiveLanguage, changeContentWidth, changeRouterTransition, changeMenuCollapsed, changeMenuHidden, changeShowLoader} =
    sagaSlice.actions;

export default sagaSlice.reducer;
